import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAuditLog() {
    const { getAuthHeaders } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await axios.get(`${API}/admin/audit-log`, {
                    headers: getAuthHeaders()
                });
                setLogs(response.data);
            } catch (error) {
                console.error('Failed to fetch audit log:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [getAuthHeaders]);

    const getActionBadge = (action) => {
        const styles = {
            create: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
            update: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
            delete: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        };
        const labels = {
            create: 'Vytvoření',
            update: 'Úprava',
            delete: 'Smazání'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs ${styles[action] || 'bg-gray-100'}`}>
                {labels[action] || action}
            </span>
        );
    };

    const getEntityLabel = (type) => {
        const labels = {
            story: 'Povídka',
            author: 'Autor',
            user: 'Uživatel',
            promo_code: 'Promo kód'
        };
        return labels[type] || type;
    };

    return (
        <div data-testid="admin-audit-log">
            <div className="flex items-center justify-between mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold">
                    Audit log
                </h1>
                <p className="text-muted-foreground">
                    Posledních {logs.length} záznamů
                </p>
            </div>

            {/* Audit Log Table */}
            <div className="listarna-card rounded-lg overflow-hidden">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Čas</th>
                            <th>Akce</th>
                            <th>Entita</th>
                            <th>ID</th>
                            <th>Detaily</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className="text-center py-8">
                                    <div className="animate-pulse">Načítání...</div>
                                </td>
                            </tr>
                        ) : logs.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-center py-8">
                                    <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                                    <p className="text-muted-foreground">Zatím žádné záznamy</p>
                                </td>
                            </tr>
                        ) : (
                            logs.map((log) => (
                                <tr key={log.id} data-testid={`audit-row-${log.id}`}>
                                    <td className="text-muted-foreground whitespace-nowrap">
                                        {new Date(log.timestamp).toLocaleString('cs-CZ')}
                                    </td>
                                    <td>{getActionBadge(log.action)}</td>
                                    <td>{getEntityLabel(log.entity_type)}</td>
                                    <td className="font-mono text-sm text-muted-foreground">
                                        {log.entity_id.slice(0, 8)}...
                                    </td>
                                    <td className="max-w-xs truncate text-sm text-muted-foreground">
                                        {log.details && Object.keys(log.details).length > 0 
                                            ? JSON.stringify(log.details).slice(0, 50) + '...'
                                            : '-'}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
